import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { useTranslation } from "next-i18next";
import { forwardRef, useImperativeHandle, useState } from "react";

export type ConfirmationDialogShowArgs = {
	text: string;
	onYes: () => void;
	onNo?: () => void;
	className?: string;
};

export type ConfirmationDialogHandle = {
	show: (args: ConfirmationDialogShowArgs) => void;
	close: () => void;
};

const ConfirmationDialog = forwardRef<ConfirmationDialogHandle>((_props, ref) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<ConfirmationDialogShowArgs | null>(null);

	// expose imperative API
	useImperativeHandle(
		ref,
		() => ({
			show: (args: ConfirmationDialogShowArgs) => {
				setOptions(args);
				setOpen(true);
			},
			close: () => {
				handleCloseInternal(false);
			},
		}),
		[],
	);

	const handleCloseInternal = (invokeNoCallback: boolean) => {
		if (invokeNoCallback && options?.onNo) {
			options.onNo();
		}
		setOpen(false);
		setOptions(null);
	};

	const handleYesClick = () => {
		if (options?.onYes) {
			options.onYes();
		}
		setOpen(false);
		setOptions(null);
	};

	const handleNoClick = () => {
		handleCloseInternal(true);
	};

	return (
		<Dialog
			open={open}
			data-testid="confirmation-dialog"
			aria-describedby="alert-dialog-description"
			className={options?.className}>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">{options?.text}</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button data-testid="yes-button" onClick={handleYesClick}>
					{t("yes")}
				</Button>
				<Button data-testid="no-button" onClick={handleNoClick} autoFocus>
					{t("no")}
				</Button>
			</DialogActions>
		</Dialog>
	);
});

ConfirmationDialog.displayName = "ConfirmationDialog";

export default ConfirmationDialog;
